import { lazy, ReactElement } from 'react'
import { Helmet } from 'react-helmet-async'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import RenewalLayout from 'components/Renewal/RenewalLayout'
import { RENEWAL_AT_ROOT, renewalPath } from 'components/Renewal/paths'
import RenewalHomePage from 'pages/RenewalHomePage'

const RenewalApplyPage = lazy(() => import('pages/RenewalApplyPage'))
const RenewalFaqPage = lazy(() => import('pages/RenewalFaqPage'))
const RenewalBusinessPage = lazy(() => import('pages/RenewalBusinessPage'))
const RenewalCaseArticlePage = lazy(() => import('pages/RenewalCaseArticlePage'))
const RenewalCasesPage = lazy(() => import('pages/RenewalCasesPage'))
const RenewalIndustriesPage = lazy(() => import('pages/RenewalIndustriesPage'))
const RenewalIndustryPage = lazy(() => import('pages/RenewalIndustryPage'))
const RenewalMapPage = lazy(() => import('pages/RenewalMapPage'))
const RenewalPressPage = lazy(() => import('pages/RenewalPressPage'))
const RenewalPricingPage = lazy(() => import('pages/RenewalPricingPage'))
const RenewalServicePage = lazy(() => import('pages/RenewalServicePage'))
const RenewalShopPage = lazy(() => import('pages/RenewalShopPage'))
const RenewalStoryPage = lazy(() => import('pages/RenewalStoryPage'))

const LEGACY_TO_RENEWAL: Array<[string, string]> = [
  ['/service-detail/self-laundry', '/industries/laundromat'],
  ['/service-detail/ice-cream', '/industries/ice-cream'],
  ['/service-detail/study-cafe', '/industries/study-cafe'],
  ['/service-detail/convenience', '/industries/unmanned-store'],
  ['/service-detail/cafe', '/industries/unmanned-cafe'],
  ['/service-detail/stationery', '/industries/stationery'],
  ['/service-detail/studio', '/industries/photo-booth'],
  ['/service-detail/pinter-machine', '/industries/print-shop'],
  ['/service-detail/smart-machine', '/industries/vending-mealkit'],
  ['/easy-consulting', '/apply'],
  ['/muin-consulting', '/apply'],
]

function KeepLocation({ pathname }: { pathname: string }) {
  const location = useLocation()
  return <Redirect to={{ pathname, search: location.search, hash: location.hash }} />
}

function StripRenewalPrefix() {
  const location = useLocation()
  const pathname = location.pathname.replace(/^\/renewal(?=\/|$)/, '') || '/'
  return <KeepLocation pathname={pathname} />
}

function pageRoute(subpath: string, element: ReactElement) {
  const slashed = renewalPath(subpath)
  const bare = slashed.length > 1 ? slashed.replace(/\/$/, '') : slashed
  const routes = [
    <Route key={bare} path={bare} exact>
      {element}
    </Route>,
  ]
  if (bare !== '/' && !bare.includes(':')) {
    routes.push(
      <Route key={`${bare}/`} path={`${bare}/`} exact>
        {element}
      </Route>
    )
  }
  return routes
}

export function renewalRouteElements() {
  const routes: ReactElement[] = [
    ...pageRoute('/', <RenewalHomePage />),
    ...pageRoute('/press', <RenewalPressPage />),
    ...pageRoute('/industries', <RenewalIndustriesPage />),
    ...pageRoute('/industries/:slug', <RenewalIndustryPage />),
    ...pageRoute('/cases', <RenewalCasesPage />),
    ...pageRoute('/cases/:slug', <RenewalCaseArticlePage />),
    ...pageRoute('/pricing', <RenewalPricingPage />),
    ...pageRoute('/snackberry', <RenewalServicePage />),
    ...pageRoute('/add-on/sign', <RenewalServicePage />),
    ...pageRoute('/add-on/aircon', <RenewalServicePage />),
    ...pageRoute('/add-on/window', <RenewalServicePage />),
    ...pageRoute('/add-on/dryer', <RenewalServicePage />),
    ...pageRoute('/supply', <RenewalServicePage />),
    ...pageRoute('/cs', <RenewalServicePage />),
    ...pageRoute('/business', <RenewalBusinessPage />),
    ...pageRoute('/faq', <RenewalFaqPage />),
    ...pageRoute('/shop', <RenewalShopPage />),
    ...pageRoute('/story', <RenewalStoryPage />),
    ...pageRoute('/apply', <RenewalApplyPage />),
    ...pageRoute('/map', <RenewalMapPage />),
    <Route key="map-store" path="/map/:storeId">
      <RenewalMapPage />
    </Route>,
  ]

  if (RENEWAL_AT_ROOT) {
    LEGACY_TO_RENEWAL.forEach(([from, to]) => {
      routes.push(
        <Route key={from} path={from} exact>
          <KeepLocation pathname={renewalPath(to)} />
        </Route>
      )
    })
    routes.push(
      <Route key="renewal-prefix" path="/renewal">
        <StripRenewalPrefix />
      </Route>
    )
  } else {
    routes.push(
      <Route key="renewal-unknown" path="/renewal">
        <UpcomingPage />
      </Route>
    )
  }

  return routes
}

export function RenewalNotFound() {
  return (
    <RenewalLayout>
      <Helmet>
        <title>없는 주소 | 브라우니</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Main>
        <h1>요청한 주소를 찾을 수 없습니다</h1>
      </Main>
    </RenewalLayout>
  )
}

export default function RenewalRoutes() {
  return <Switch>{renewalRouteElements()}</Switch>
}

function UpcomingPage() {
  const { pathname } = useLocation()

  return (
    <RenewalLayout>
      <Helmet>
        <title>준비 중 | 브라우니</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Main>
        <h1>이 페이지는 이어서 만듭니다</h1>
        <p>{pathname}</p>
      </Main>
    </RenewalLayout>
  )
}

const Main = styled.main`
  max-width: 720px;
  margin: 0 auto;
  padding: clamp(72px, 8vw, 120px) 20px;
  text-align: center;

  h1 {
    font-size: clamp(28px, 3.4vw, 40px);
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  p {
    margin-top: 12px;
    color: #4e5968;
  }
`
